import {
  ATFilterConfigItemType,
  ATFilterConfigComponentType,
  FilterOperatorsType,
  FilterComponentType,
  FilterPredicateType,
} from '../actionTable/ATTypes';
import {
  ATColumnType,
  ATFilterItemType,
  ATFilterItemsType,
} from '../hooks';

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** Filter Processing Logic - Moved from ActionTableMain.tsx

// Utility function to normalize strings for comparison
export const normalizeString = (value: any): string => {
  return String(value).trim().toLowerCase();
};

// Utility function to normalize dates for comparison
export const normalizeDate = (value: any): string => {
  if (!value) return '';

  // First, normalize the string by replacing different dash types with standard hyphen
  const normalizedString = String(value).replace(/[‑–—]/g, '-');

  const date = new Date(normalizedString);
  return isNaN(date.getTime())
    ? normalizedString
    : date.toISOString().split('T')[0];
};

// Helper function to check if a value is a date
export const isDateValue = (value: any): boolean => {
  if (!value) return false;
  const normalizedString = String(value).replace(/[‑–—]/g, '-');
  const date = new Date(normalizedString);
  return !isNaN(date.getTime()) && normalizedString.includes('-');
};

// Compare function for filter operations
export const compare = (
  raw: any,
  operator: string | string[] | undefined,
  value: any,
): boolean => {
  const op = operator ?? 'contains';

  if (Array.isArray(value)) {
    const selected = value.map((v) => normalizeString(v));
    const operators = Array.isArray(op) ? op : [op];

    if (Array.isArray(raw)) {
      const rawVals = raw.map((v) => normalizeString(v));
      return rawVals.some((rv) => {
        return selected.some((sv, index) => {
          const currentOp = operators[index] || operators[0] || 'contains';
          const hasMatch = rv.includes(sv);
          return currentOp === 'notcontains' ? !hasMatch : hasMatch;
        });
      });
    }

    const rawStr = normalizeString(raw);
    return selected.some((sv, index) => {
      const currentOp = operators[index] || operators[0] || 'contains';
      const hasMatch = rawStr.includes(sv);
      return currentOp === 'notcontains' ? !hasMatch : hasMatch;
    });
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return Boolean(raw) === value;
  }

  // Parse number/date when needed
  const asNumber = (x: any): number | null => {
    const n = typeof x === 'number' ? x : Number(x);
    return Number.isFinite(n) ? n : null;
  };
  const asDate = (x: any): number | null => {
    const t = new Date(x as any).getTime();
    return Number.isFinite(t) ? t : null;
  };

  switch (op) {
    case 'equal':
      // Check if values are dates and normalize accordingly
      if (isDateValue(raw) || isDateValue(value)) {
        return normalizeDate(raw) === normalizeDate(value);
      }
      return normalizeString(raw) === normalizeString(value);
    case 'notequal':
      // Check if values are dates and normalize accordingly
      if (isDateValue(raw) || isDateValue(value)) {
        return normalizeDate(raw) !== normalizeDate(value);
      }
      return normalizeString(raw) !== normalizeString(value);
    case 'startswith':
      return normalizeString(raw).startsWith(normalizeString(value));
    case 'notstartswith':
      return !normalizeString(raw).startsWith(normalizeString(value));
    case 'endswith':
      return normalizeString(raw).endsWith(normalizeString(value));
    case 'notendswith':
      return !normalizeString(raw).endsWith(normalizeString(value));
    case 'contains':
      return normalizeString(raw).includes(normalizeString(value));
    case 'notcontains':
      return !normalizeString(raw).includes(normalizeString(value));
    case 'greaterthan': {
      // try number then date
      const rn = asNumber(raw);
      const vn = asNumber(value);
      if (rn !== null && vn !== null) return rn > vn;
      const rd = asDate(raw);
      const vd = asDate(value);
      if (rd !== null && vd !== null) return rd > vd;
      return false;
    }
    case 'greaterthanorequal': {
      const rn = asNumber(raw);
      const vn = asNumber(value);
      if (rn !== null && vn !== null) return rn >= vn;
      const rd = asDate(raw);
      const vd = asDate(value);
      if (rd !== null && vd !== null) return rd >= vd;
      return false;
    }
    case 'lessthan': {
      const rn = asNumber(raw);
      const vn = asNumber(value);
      if (rn !== null && vn !== null) return rn < vn;
      const rd = asDate(raw);
      const vd = asDate(value);
      if (rd !== null && vd !== null) return rd < vd;
      return false;
    }
    case 'lessthanorequal': {
      const rn = asNumber(raw);
      const vn = asNumber(value);
      if (rn !== null && vn !== null) return rn <= vn;
      const rd = asDate(raw);
      const vd = asDate(value);
      if (rd !== null && vd !== null) return rd <= vd;
      return false;
    }
    default:
      return normalizeString(raw).includes(normalizeString(value));
  }
};

// Process advanced filters
export const processAdvancedFilters = <T extends Record<string, any>>(
  items: T[],
  filtersAdvanced: ATFilterItemsType<T>,
): T[] => {
  if (!filtersAdvanced || Object.keys(filtersAdvanced).length === 0) {
    return items;
  }

  // Group filters by itemKey to handle filters that span multiple columns
  const filtersByItemKey: Record<string, any[]> = {};

  Object.entries(filtersAdvanced).forEach(([colKey, filterItems]: any) => {
    if (!filterItems || Object.keys(filterItems).length === 0) {
      return; // Skip empty filters
    }

    // Process each filter item in this column
    Object.values(filterItems).forEach((filterValue: any) => {
      const isActive = !(
        filterValue.value === undefined ||
        filterValue.value === null ||
        filterValue.value === '' ||
        filterValue.value === false ||
        (Array.isArray(filterValue.value) && filterValue.value.length === 0)
      );

      if (isActive) {
        // Use component-level predicate to determine grouping
        // Extract component index from itemKey (format: "componentIndex_itemIndex")
        const componentIndex = filterValue.itemKey.split('_')[0];

        // Check if this component should use OR logic for multiple selections
        // This will be determined by the filterComponentPredicate in the filter configuration
        const useComponentOrLogic =
          filterValue.filterComponentPredicate === 'or';

        const groupKey = useComponentOrLogic
          ? `component_${componentIndex}`
          : filterValue.itemKey;

        if (!filtersByItemKey[groupKey]) {
          filtersByItemKey[groupKey] = [];
        }
        filtersByItemKey[groupKey].push({
          ...filterValue,
          colKey,
        });
      }
    });
  });

  return items.filter((item, index) => {
    const result = Object.entries(filtersByItemKey).every(
      ([itemKey, filterColumns]) => {
        if (filterColumns.length === 0) {
          return true;
        }

        const useOrLogic = filterColumns.some(
          (filterValue: any) => filterValue.predicate === 'or',
        );

        if (useOrLogic) {
          const itemResult = filterColumns.some((filterValue: any) => {
            const raw = item[filterValue.colKey as string];

            const hasEmptyValueFilter = filterValue.filterEmptyValue === true;

            if (raw == null) {
              return hasEmptyValueFilter;
            }

            const compareResult = compare(
              raw,
              filterValue.operator,
              filterValue.value,
            );
            return compareResult;
          });
          return itemResult;
        } else {
          const itemResult = filterColumns.every((filterValue: any) => {
            const raw = item[filterValue.colKey as string];

            const hasEmptyValueFilter = filterValue.filterEmptyValue === true;

            if (raw == null) {
              return hasEmptyValueFilter;
            }

            const compareResult = compare(
              raw,
              filterValue.operator,
              filterValue.value,
            );
            return compareResult;
          });
          return itemResult;
        }
      },
    );

    return result;
  });
};

// Process global search filter
export const processGlobalSearch = <T extends Record<string, any>>(
  items: T[],
  filterValue: string,
  filterCols: ATColumnType<T>[],
): T[] => {
  if (!filterValue) {
    return items;
  }

  return items.filter((item) => {
    return filterCols.some((itemColKey) => {
      const raw = item[itemColKey as string];
      return (
        raw != null &&
        normalizeString(raw).includes(normalizeString(filterValue))
      );
    });
  });
};

// Main filter processing function
export const processFilters = <T extends Record<string, any>>(
  items: T[],
  filterValue: string,
  filterCols: ATColumnType<T>[],
  filtersAdvanced: ATFilterItemsType<T>,
): T[] => {
  let filtered = items;

  // Apply global search filter
  filtered = processGlobalSearch(filtered, filterValue, filterCols);

  // Apply column-specific filters
  filtered = processAdvancedFilters(filtered, filtersAdvanced);

  return filtered;
};

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** initialiseFilterConfig - Initialise the filter config

export type FilterConfigComponentType<T extends Record<string, any>> =
  ATFilterConfigComponentType<T> & {
    componentLabel: string;
    componentKey: string;
    componentValue: any;
    componentDefaultValue: string | number | boolean | string[];
  };
export const initialiseFilterConfig = <T extends Record<string, any>>(
  filterConfigComponents: ATFilterConfigComponentType<T>[],
  columns: ATColumnType<T>[],
): {
  tempFilterItems: ATFilterItemsType<T>;
  tempFilterConfigComponents: FilterConfigComponentType<T>[];
} => {
  const tempFilterItems: ATFilterItemsType<T> = {};

  const tempFilterConfigComponents: FilterConfigComponentType<T>[] = [];

  const addFilterItem = (
    colKeys: ATColumnType<T>[],
    newItemKey: string,
    item: ATFilterConfigItemType<T>,
    componentItem: ATFilterConfigComponentType<T>,
  ): ATFilterConfigItemType<T> => {
    const normalisedItem: ATFilterConfigItemType<T> = {
      ...item,
      colKeys: colKeys,
      value: item.value as string | number | boolean,
    };

    // Add to tempFilterItems if:
    // 1. For selection components (select, combobox, checkbox, switch): only if autoFilter is true
    // 2. For other components: if value is not empty/null/undefined OR filterEmptyValue is true
    const isSelectionComponent =
      componentItem.filterComponent === 'select' ||
      componentItem.filterComponent === 'combobox' ||
      componentItem.filterComponent === 'checkbox' ||
      componentItem.filterComponent === 'switch';

    const shouldAddToFilter = isSelectionComponent
      ? item.autoFilter === true
      : (item.value !== null &&
          item.value !== undefined &&
          item.value !== '') ||
        item.filterEmptyValue === true;

    if (shouldAddToFilter) {
      // Add the filter to each specified column with the correct colKey
      colKeys.forEach((colKey) => {
        const tempItem: ATFilterItemType<T> = {
          value: item.value,
          itemKey: newItemKey,
          colKey: colKey,
          operator:
            item.operator ??
            (componentItem.filterComponent === 'switch' ? 'equal' : 'contains'),
          predicate: item.predicate ?? 'or',
          matchCase: item.matchCase ?? false,
          ignoreAccents: item.ignoreAccents ?? false,
          filterEmptyValue: item.filterEmptyValue ?? false,
          filterComponentPredicate:
            componentItem.filterComponentPredicate ?? 'or',
        };

        if (!tempFilterItems[colKey]) {
          tempFilterItems[colKey] = {};
        }
        (
          tempFilterItems[colKey] as Record<
            ATFilterItemType<T>['itemKey'],
            ATFilterItemType<T>
          >
        )[newItemKey] = tempItem;
      });
    }

    return normalisedItem;
  };

  if (filterConfigComponents.length > 0) {
    filterConfigComponents.forEach(
      (
        componentItem: ATFilterConfigComponentType<T>,
        componentIndex: number,
      ) => {
        const isSelectionComponent =
          componentItem.filterComponent === 'select' ||
          componentItem.filterComponent === 'combobox' ||
          componentItem.filterComponent === 'checkbox';

        let componentLabel = componentItem.filterComponentLabel ?? '';

        let componentValue: any = isSelectionComponent
          ? []
          : { value: undefined, itemKey: '' };
        let componentDefaultValue: any = isSelectionComponent ? [] : undefined;

        const tempFilterConfigItems: ATFilterConfigItemType<T>[] = [];

        const initialiseItemConfig = (
          item: ATFilterConfigItemType<T>,
          componentIndex: number,
          itemIndex: number,
        ): {
          itemColKeys: ATColumnType<T>[];
          newItemKey: string;
          componentLabel: string;
        } => {
          const itemColKeys =
            item.colKeys && item.colKeys.length > 0
              ? item.colKeys
              : componentItem.defaultColKeys &&
                  componentItem.defaultColKeys.length > 0
                ? componentItem.defaultColKeys
                : [columns[0] as ATColumnType<T>];
          componentLabel =
            componentItem.filterComponentLabel ??
            itemColKeys
              .map((colKey) =>
                colKey
                  .split('_')
                  .map(
                    (word) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(' '),
              )
              .join(', ');
          let newItemKey: string = `${componentIndex}_${item.itemKey ?? itemIndex}`;

          return { itemColKeys, newItemKey, componentLabel };
        };

        if (
          componentItem.filterConfigItems &&
          componentItem.filterConfigItems.length > 0
        ) {
          componentItem.filterConfigItems.forEach(
            (item: ATFilterConfigItemType<T>, itemIndex: number) => {
              const { itemColKeys, newItemKey, componentLabel } =
                initialiseItemConfig(item, componentIndex, itemIndex);

              // Process each item based on component type
              switch (componentItem.filterComponent) {
                case 'select':
                case 'combobox':
                case 'checkbox': {
                  // Selection components (select, combobox, checkbox)
                  const normalisedItem: ATFilterConfigItemType<T> =
                    addFilterItem(
                      itemColKeys,
                      newItemKey,
                      {
                        ...item,
                        itemKey: newItemKey,
                      },
                      componentItem,
                    );

                  // Always add to tempFilterConfigItems
                  tempFilterConfigItems.push(normalisedItem);

                  // Add to componentValue for UI display
                  componentValue.push({
                    label: item.label || String(item.value),
                    value: String(item.value),
                    itemKey: newItemKey,
                    item: normalisedItem,
                  });

                  // Set default value based on conditions
                  if (componentItem.isSelectAllOption === true) {
                    // Select All: Include all items by default
                    componentDefaultValue.push(item.value);
                  } else if (item.autoFilter === true) {
                    // Auto Filter: Include items that should be auto-filtered
                    componentDefaultValue.push(item.value);
                  }
                  break;
                }

                case 'input': {
                  // Text input component

                  componentDefaultValue = String(item.value) ?? '';
                  const normalisedItem: ATFilterConfigItemType<T> =
                    addFilterItem(
                      itemColKeys,
                      newItemKey,
                      {
                        ...item,
                        itemKey: newItemKey,
                      },
                      componentItem,
                    );
                  tempFilterConfigItems.push(normalisedItem);
                  componentValue = {
                    value: String(item.value),
                    itemKey: newItemKey,
                    item: normalisedItem,
                  };
                  break;
                }

                case 'date': {
                  // Date input component
                  componentDefaultValue = normalizeDate(item.value) ?? '';
                  const normalisedItem: ATFilterConfigItemType<T> =
                    addFilterItem(
                      itemColKeys,
                      newItemKey,
                      {
                        ...item,
                        itemKey: newItemKey,
                      },
                      componentItem,
                    );
                  tempFilterConfigItems.push(normalisedItem);
                  componentValue = {
                    value: String(item.value),
                    itemKey: newItemKey,
                    item: normalisedItem,
                  };
                  break;
                }

                case 'number': {
                  // Number input component
                  componentDefaultValue = Number(item.value) ?? 0;
                  const normalisedItem: ATFilterConfigItemType<T> =
                    addFilterItem(
                      itemColKeys,
                      newItemKey,
                      { ...item, itemKey: newItemKey },
                      componentItem,
                    );
                  tempFilterConfigItems.push(normalisedItem);
                  componentValue = {
                    value: Number(item.value),
                    itemKey: newItemKey,
                    item: normalisedItem,
                  };
                  break;
                }

                case 'switch': {
                  // Switch/toggle component
                  componentDefaultValue =
                    item.autoFilter === true ? true : false;

                  // Only add filter item if autoFilter is true
                  let normalisedItem: ATFilterConfigItemType<T>;
                  if (item.autoFilter === true) {
                    normalisedItem = addFilterItem(
                      itemColKeys,
                      newItemKey,
                      { ...item, itemKey: newItemKey },
                      componentItem,
                    );
                  } else {
                    // Create item without adding to filters
                    normalisedItem = {
                      ...item,
                      colKeys: itemColKeys,
                      value: item.value as string | number | boolean,
                      itemKey: newItemKey,
                    };
                  }

                  tempFilterConfigItems.push(normalisedItem);
                  componentValue = {
                    value: item.autoFilter === true ? true : false,
                    itemKey: newItemKey,
                    item: normalisedItem,
                  };
                  break;
                }

                default: {
                  // Unknown component type - skip
                  break;
                }
              }
            },
          );
        }

        tempFilterConfigComponents.push({
          ...componentItem,
          filterConfigItems: tempFilterConfigItems,
          componentLabel: componentLabel,
          componentKey: `${componentItem.filterComponentKey ?? componentItem.filterComponent}-${componentIndex}`,
          componentValue,
          componentDefaultValue,
        });
      },
    );
  }
  return { tempFilterItems, tempFilterConfigComponents };
};

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** handleChangeInput - Handle change of input filter
export const handleChangeInput = (
  value: string,
  itemKey: string,
  colKeys: ATColumnType<any>[],
  operator: FilterOperatorsType = 'contains',
  setFiltersAdvanced: (fn: (prev: any) => any) => void,
  filterComponent: FilterComponentType = 'input',
) => {
  if (value && value.trim() !== '') {
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (!newState[colKey]) {
          newState[colKey] = {};
        }

        // Remove existing filter with same itemKey
        if (newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }

        // Add new filter
        newState[colKey][itemKey] = {
          value: filterComponent === 'date' ? normalizeDate(value) : value,
          itemKey: itemKey,
          colKey: colKey,
          operator: operator,
          predicate: 'or' as const,
        };
      });

      return newState;
    });
  } else {
    // Remove the filter if empty
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (newState[colKey] && newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }
      });

      return newState;
    });
  }
};

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** handleChangeNumber - Handle change of number filter
export const handleChangeNumber = (
  value: number,
  itemKey: string,
  colKeys: ATColumnType<any>[],
  operator: FilterOperatorsType = 'contains',
  setFiltersAdvanced: (fn: (prev: any) => any) => void,
) => {
  if (value !== null && value !== undefined && !isNaN(value)) {
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (!newState[colKey]) {
          newState[colKey] = {};
        }

        // Remove existing filter with same itemKey
        if (newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }

        // Add new filter
        newState[colKey][itemKey] = {
          value: String(value),
          itemKey: itemKey,
          colKey: colKey,
          operator: operator,
          predicate: 'or' as const,
        };
      });

      return newState;
    });
  } else {
    // Remove the filter if empty/invalid
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (newState[colKey] && newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }
      });

      return newState;
    });
  }
};

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** handleChangeSwitch - Handle change of switch filter
export const handleChangeSwitch = (
  checked: boolean,
  itemKey: string,
  colKeys: ATColumnType<any>[],
  operator: FilterOperatorsType = 'equal',
  setFiltersAdvanced: (fn: (prev: any) => any) => void,
  filterValue?: any,
) => {
  if (String(checked) === 'true' || checked === true) {
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (!newState[colKey]) {
          newState[colKey] = {};
        }

        if (newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }

        newState[colKey][itemKey] = {
          value: filterValue !== undefined ? filterValue : checked,
          itemKey: itemKey,
          colKey: colKey,
          operator: operator,
          predicate: 'or' as const,
        };
      });

      return newState;
    });
  } else {
    // Remove the filter when unchecked
    setFiltersAdvanced((prev) => {
      const newState = { ...prev };

      colKeys.forEach((colKey) => {
        if (newState[colKey] && newState[colKey][itemKey]) {
          delete newState[colKey][itemKey];
        }
      });

      return newState;
    });
  }
};

// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ** handleChangeSelect - Handle change of select filter
export const handleChangeSelect = (
  items: { value: any; itemKey: string; item: ATFilterConfigItemType<any> }[],
  setFiltersAdvanced: (fn: (prev: any) => any) => void,
  componentIndex: number,
  filterComponentPredicate: FilterPredicateType = 'or',
  defaultColKeys?: ATColumnType<any>[],
  columns?: ATColumnType<any>[],
) => {
  setFiltersAdvanced((prev) => {
    const newState = { ...prev };

    Object.keys(newState).forEach((colKey) => {
      if (newState[colKey]) {
        Object.keys(newState[colKey]).forEach((key) => {
          if (key.startsWith(`${componentIndex}_`)) {
            delete newState[colKey][key];
          }
        });
      }
    });

    if (items.length > 0) {
      items.forEach(
        (selectedItem: {
          value: any;
          itemKey: string;
          item: ATFilterConfigItemType<any>;
        }) => {
          const { itemKey, value, item } = selectedItem;

          // Determine colKeys: use item.colKeys if set, otherwise fall back to defaultColKeys or columns[0]
          const colKeysToUse: ATColumnType<any>[] =
            item.colKeys && item.colKeys.length > 0
              ? item.colKeys
              : defaultColKeys && defaultColKeys.length > 0
                ? defaultColKeys
                : columns && columns.length > 0
                  ? [columns[0]]
                  : [];

          // Add new filters for this item
          if (colKeysToUse.length > 0) {
            colKeysToUse.forEach((colKey) => {
              if (!newState[colKey]) {
                newState[colKey] = {};
              }

              // Add new filter
              newState[colKey][itemKey] = {
                value: String(value),
                itemKey: itemKey,
                colKey: colKey,
                operator: item.operator,
                predicate: item.predicate || 'or',
                filterComponentPredicate: filterComponentPredicate,
              };
            });
          }
        },
      );
    }

    return newState;
  });
};
